import {
  Box,
  Icon,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";

import { PreparedRecord } from "api";
import { MessageKey, useFormatMessage } from "lang";
import { formatEnergy, formatPower } from "utils/energy";

export const EnergyChartTooltip = <
  SelectedMetrics extends string =
    | "energyConsumed"
    | "energyProduced"
    | "inductiveEnergyConsumed"
    | "capacitiveEnergyProduced"
    | "inductiveEnergyProduced"
    | "capacitiveEnergyConsumed",
>({
  active,
  payload,
  formatLabel,
  formatValue,
  isPower,
  isBarChart,
  lockedIndex,
  allData,
  onPrev,
  onNext,
  minWidth = 250,
}: {
  active?: boolean;
  payload?: {
    dataKey: SelectedMetrics;
    payload: PreparedRecord<SelectedMetrics>;
    value: number;
    color: string;
    stroke: string;
  }[];
  formatLabel: (date: Date, period?: number) => string;
  formatValue: (value: number) => string;
  isPower?: boolean;
  isBarChart?: boolean;
  lockedIndex?: number | null;
  allData?: PreparedRecord<SelectedMetrics>[];
  onPrev?: () => void;
  onNext?: () => void;
  minWidth?: number;
}) => {
  const formatMessage = useFormatMessage();
  const theme = useTheme();

  const isLocked = lockedIndex !== null && lockedIndex !== undefined;

  if (!isLocked && (!active || !payload || !payload.length)) return null;
  if (!payload || !payload.length) return null;

  const effectiveRecord: PreparedRecord<SelectedMetrics> = isLocked
    ? allData![lockedIndex!]
    : payload[0].payload;

  const effectivePayload = payload.map((p) => ({
    ...p,
    value: isLocked
      ? ((effectiveRecord[p.dataKey] as number | undefined) ?? 0)
      : p.value,
    payload: effectiveRecord,
  }));

  const primaryDataKey = effectivePayload[0]?.dataKey;

  const canGoPrev = isLocked ? lockedIndex! > 0 : true;
  const canGoNext = isLocked
    ? lockedIndex! < (allData?.length ?? 1) - 1 &&
      (primaryDataKey == null ||
        allData?.[lockedIndex! + 1]?.[primaryDataKey] != null)
    : true;

  return (
    <Paper
      elevation={0}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      sx={{
        m: 1,
        border: "solid",
        borderWidth: 2,
        borderColor: "divider",
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(18, 18, 18, 0.8)"
            : "rgba(255, 255, 255, 0.9)",
        minWidth,
        table: { borderSpacing: 0, width: "100%" },
        thead: {
          td: {
            px: 0.5,
            py: 0.5,
            borderBottom: "solid",
            borderColor: "divider",
          },
        },
        tbody: {
          "tr:first-child": { td: { paddingTop: 1.5 } },
          "tr:last-child": { td: { paddingBottom: 1.5 } },
          tr: {
            "td:first-child": { paddingLeft: 1.5 },
            "td:nth-child(2)": { width: "100%" },
            "td:last-child": { paddingRight: 1.5, whiteSpace: "nowrap" },
            td: {
              paddingRight: "7px",
              paddingBottom: "10px",
            },
          },
        },
      }}
    >
      <table>
        <thead>
          <tr>
            <td colSpan={3}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.25,
                }}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPrev?.();
                  }}
                  disabled={!canGoPrev}
                  sx={{ p: 0.25 }}
                >
                  <Icon sx={{ fontSize: 18 }}>chevron_left</Icon>
                </IconButton>
                <Typography sx={{ flex: 1, textAlign: "center" }}>
                  {formatLabel(effectiveRecord.date, effectiveRecord.period)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onNext?.();
                  }}
                  disabled={!canGoNext}
                  sx={{ p: 0.25 }}
                >
                  <Icon sx={{ fontSize: 18 }}>chevron_right</Icon>
                </IconButton>
              </Box>
            </td>
          </tr>
        </thead>
        <tbody>
          {effectivePayload.map((series) => {
            const powerToEnergyForPeriod = (
              powerValue: number,
              periodMs?: number,
            ) => {
              if (!periodMs || !isPower) return null;
              const periodHours = periodMs / (1000 * 60 * 60);
              const energyValue = powerValue * periodHours;
              return formatEnergy(energyValue);
            };

            const energyToAveragePower = (
              energyValue: number,
              periodMs?: number,
            ) => {
              if (!periodMs || isPower) return null;
              const periodHours = periodMs / (1000 * 60 * 60);
              return formatPower(energyValue / periodHours);
            };

            const shouldInvert = isPower && isBarChart;

            return (
              <React.Fragment key={series.dataKey}>
                {shouldInvert && (
                  <tr>
                    <td>
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          backgroundColor: series.color,
                        }}
                      />
                    </td>
                    <td>
                      <Typography fontWeight="light">
                        {formatMessage(series.dataKey as MessageKey)}
                      </Typography>
                    </td>
                    <td>
                      <Typography>
                        {powerToEnergyForPeriod(
                          series.value,
                          series.payload.period,
                        )?.formatted || "-"}
                      </Typography>
                    </td>
                  </tr>
                )}
                {shouldInvert ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "right" }}>
                      <Typography
                        fontWeight="light"
                        sx={{ fontSize: "0.875em", opacity: 0.8 }}
                      >
                        {formatValue(series.value)}
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td>
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 2,
                          backgroundColor: series.color,
                        }}
                      />
                    </td>
                    <td>
                      <Typography fontWeight="light">
                        {`${formatMessage(series.dataKey as MessageKey)} ${isPower ? `(${formatMessage("power").toLocaleLowerCase()})` : ""}`}
                      </Typography>
                    </td>
                    <td>
                      <Typography>{formatValue(series.value)}</Typography>
                    </td>
                  </tr>
                )}
                {isPower && !shouldInvert && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "right" }}>
                      <Typography
                        fontWeight="light"
                        sx={{ fontSize: "0.875em", opacity: 0.8 }}
                      >
                        {`${powerToEnergyForPeriod(series.value, series.payload.period)?.formatted || "-"}`}
                      </Typography>
                    </td>
                    <td></td>
                  </tr>
                )}
                {!isPower && isBarChart && (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "right" }}>
                      <Typography
                        fontWeight="light"
                        sx={{ fontSize: "0.875em", opacity: 0.8 }}
                      >
                        {energyToAveragePower(
                          series.value,
                          series.payload.period,
                        )?.formatted || "-"}
                      </Typography>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </Paper>
  );
};
